import posthog from 'posthog-js'

// Environment checks
const isDev = import.meta.env.DEV
const posthogKey = import.meta.env.VITE_POSTHOG_KEY
const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

// Track if PostHog has been initialized
let isPostHogInitialized = false

/**
 * Get analytics consent from localStorage
 * This is a direct read to avoid circular dependencies with the store
 */
function getAnalyticsConsent(): boolean {
  try {
    const stored = localStorage.getItem('brightseed-cookie-consent')
    if (!stored) return false
    const { state } = JSON.parse(stored)
    return state?.hasConsented && state?.preferences?.analytics === true
  } catch {
    return false
  }
}

/**
 * Initialize PostHog for analytics and error tracking
 * Only initializes if user has given consent
 */
export function initObservability() {
  if (!posthogKey) {
    if (!isDev) console.warn('PostHog key not configured')
    return
  }

  const hasConsent = getAnalyticsConsent()

  if (hasConsent && !isPostHogInitialized) {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      // Don't track in development
      loaded: (ph) => {
        if (isDev) ph.opt_out_capturing()
      },
      // Respect Do Not Track browser setting
      respect_dnt: true,
      // Capture pageviews automatically
      capture_pageview: true,
      // Capture page leaves
      capture_pageleave: true,
      // Session replay - enabled in production only
      disable_session_recording: isDev,
      // Error tracking - auto-capture unhandled errors
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
    })
    isPostHogInitialized = true
    console.log('PostHog initialized (analytics + session replay + error tracking)')
  } else if (!hasConsent && isPostHogInitialized) {
    // User revoked consent - opt out
    posthog.opt_out_capturing()
    console.log('PostHog disabled (consent revoked)')
  } else if (!hasConsent) {
    console.log('PostHog not initialized (no consent)')
  }
}

/**
 * Re-check consent and initialize/disable PostHog accordingly
 * Call this when user updates their cookie preferences
 */
export function updateObservabilityConsent() {
  const hasConsent = getAnalyticsConsent()

  if (hasConsent && posthogKey) {
    if (!isPostHogInitialized) {
      // Initialize for the first time
      posthog.init(posthogKey, {
        api_host: posthogHost,
        loaded: (ph) => {
          if (isDev) ph.opt_out_capturing()
        },
        respect_dnt: true,
        capture_pageview: true,
        capture_pageleave: true,
        disable_session_recording: isDev,
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
      })
      isPostHogInitialized = true
      console.log('PostHog initialized after consent (with session replay)')
    } else {
      // Re-enable if previously opted out
      posthog.opt_in_capturing()
      console.log('PostHog re-enabled')
    }
  } else if (!hasConsent && isPostHogInitialized) {
    // Disable tracking
    posthog.opt_out_capturing()
    console.log('PostHog disabled')
  }
}

/**
 * User properties for identification
 */
export interface UserProperties {
  email?: string
  name?: string
  role?: 'childminder' | 'parent'
  plan?: 'free' | 'starter' | 'professional'
  signupDate?: string
  childCount?: number
  parentCount?: number
  hasActiveSubscription?: boolean
  emailVerified?: boolean
}

/**
 * Identify a user for analytics with rich properties
 * Call this on login/signup and when user data changes
 */
export function identifyUser(userId: string, properties?: UserProperties) {
  if (isPostHogInitialized && getAnalyticsConsent()) {
    // Use PostHog's special $ prefixed properties for display
    const posthogProperties: Record<string, unknown> = {
      // PostHog display properties (shown in UI)
      $email: properties?.email,
      $name: properties?.name,
      // Custom properties
      email: properties?.email,
      name: properties?.name,
      role: properties?.role,
      plan: properties?.plan,
      childCount: properties?.childCount,
      parentCount: properties?.parentCount,
      hasActiveSubscription: properties?.hasActiveSubscription,
      emailVerified: properties?.emailVerified,
    }

    posthog.identify(userId, posthogProperties)

    // Set once properties (only set on first identification)
    posthog.people.set_once({
      first_seen: new Date().toISOString(),
      signup_date: properties?.signupDate,
    })
  }
}

/**
 * Update user properties without re-identifying
 * Use for incremental property updates
 */
export function setUserProperties(properties: Partial<UserProperties>) {
  if (isPostHogInitialized && getAnalyticsConsent()) {
    // Map to PostHog properties, including $ prefixed display properties
    const posthogProperties: Record<string, unknown> = { ...properties }
    if (properties.email) posthogProperties.$email = properties.email
    if (properties.name) posthogProperties.$name = properties.name
    posthog.people.set(posthogProperties)
  }
}

/**
 * Note: For counting user actions (updates created, messages sent, etc.),
 * use PostHog's event counting in Insights instead of user properties.
 * PostHog will automatically track counts per user based on events.
 */

/**
 * Associate user with a childminder group
 * This enables B2B-style analytics - see all parents under a childminder
 */
export function setChildminderGroup(childminderId: string, childminderName?: string) {
  if (isPostHogInitialized && getAnalyticsConsent()) {
    posthog.group('childminder', childminderId, {
      name: childminderName,
    })
  }
}

/**
 * Clear user identity on logout
 */
export function clearUserIdentity() {
  if (isPostHogInitialized) {
    posthog.reset()
  }
}

/**
 * Set properties that persist across all events in a session
 * Useful for context that should be on every event
 */
export function setSuperProperties(properties: Record<string, unknown>) {
  if (isPostHogInitialized && getAnalyticsConsent()) {
    posthog.register(properties)
  }
}

/**
 * Remove super properties
 */
export function clearSuperProperties(propertyNames: string[]) {
  if (isPostHogInitialized) {
    propertyNames.forEach((name) => posthog.unregister(name))
  }
}

/**
 * Track a custom event (only if consent given)
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (isPostHogInitialized && getAnalyticsConsent() && !isDev) {
    posthog.capture(eventName, properties)
  }
}

/**
 * Capture an error to PostHog (only if consent given)
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  console.error(error)
  if (isPostHogInitialized && getAnalyticsConsent() && !isDev) {
    posthog.captureException(error, context)
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, category?: string, data?: Record<string, unknown>) {
  if (isDev) {
    console.log(`[${category || 'app'}] ${message}`, data || '')
  }
}

// ============================================
// Logging
// ============================================

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

/**
 * Send a log entry to PostHog
 * Logs are captured as '$log' events for the Logs feature
 */
function sendLog(level: LogLevel, message: string, context?: Record<string, unknown>) {
  // Always log to console
  const consoleMethod = level === 'debug' ? 'log' : level
  console[consoleMethod](`[${level.toUpperCase()}] ${message}`, context || '')

  // Only send to PostHog if initialized and consent given (skip debug in prod)
  if (!isPostHogInitialized || !getAnalyticsConsent()) return
  if (level === 'debug' && !isDev) return

  posthog.capture('$log', {
    $log_level: level,
    $log_message: message,
    $log_source: 'frontend',
    ...context,
  })
}

/**
 * Frontend logger that sends structured logs to PostHog
 */
export const log = {
  debug(message: string, context?: Record<string, unknown>) {
    sendLog('debug', message, context)
  },

  info(message: string, context?: Record<string, unknown>) {
    sendLog('info', message, context)
  },

  warn(message: string, context?: Record<string, unknown>) {
    sendLog('warn', message, context)
  },

  error(message: string, error?: Error | unknown, context?: Record<string, unknown>) {
    const errorContext = {
      ...context,
      ...(error instanceof Error ? {
        error_name: error.name,
        error_message: error.message,
        error_stack: error.stack,
      } : error ? { error: String(error) } : {}),
    }
    sendLog('error', message, errorContext)
  },
}

// ============================================
// Feature Flags
// ============================================

/**
 * Check if a feature flag is enabled
 * Returns false if PostHog isn't initialized or no consent
 */
export function isFeatureEnabled(flagKey: string): boolean {
  if (!isPostHogInitialized || !getAnalyticsConsent()) {
    return false
  }
  return posthog.isFeatureEnabled(flagKey) ?? false
}

/**
 * Get feature flag value (for multivariate flags)
 */
export function getFeatureFlag(flagKey: string): string | boolean | undefined {
  if (!isPostHogInitialized || !getAnalyticsConsent()) {
    return undefined
  }
  return posthog.getFeatureFlag(flagKey)
}

/**
 * Get feature flag payload (additional data attached to flag)
 */
export function getFeatureFlagPayload(flagKey: string): unknown {
  if (!isPostHogInitialized || !getAnalyticsConsent()) {
    return undefined
  }
  return posthog.getFeatureFlagPayload(flagKey)
}

/**
 * Reload feature flags (useful after user properties change)
 */
export function reloadFeatureFlags(): Promise<void> {
  return new Promise((resolve) => {
    if (!isPostHogInitialized || !getAnalyticsConsent()) {
      resolve()
      return
    }
    posthog.reloadFeatureFlags()
    // PostHog doesn't return a promise, so we wait a bit
    setTimeout(resolve, 500)
  })
}

/**
 * Subscribe to feature flag changes
 */
export function onFeatureFlagsLoaded(callback: () => void): () => void {
  if (!isPostHogInitialized) {
    return () => {}
  }
  posthog.onFeatureFlags(callback)
  return () => {
    // PostHog doesn't have an unsubscribe, but the callback won't fire after reset
  }
}

// Pre-defined feature flags for type safety
export const FeatureFlags = {
  // Example flags - add your actual flags here
  NEW_DASHBOARD: 'new-dashboard',
  ENHANCED_MESSAGING: 'enhanced-messaging',
  BULK_PHOTO_UPLOAD: 'bulk-photo-upload',
  ADVANCED_CALENDAR: 'advanced-calendar',
  DARK_MODE: 'dark-mode',
} as const

// Pre-defined events for consistency
export const Events = {
  // Auth events
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  LOGIN: 'login',
  LOGOUT: 'logout',
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
  EMAIL_VERIFIED: 'email_verified',

  // Core feature events - Updates
  UPDATE_CREATED: 'update_created',
  UPDATE_VIEWED: 'update_viewed',
  UPDATE_FORM_OPENED: 'update_form_opened',

  // Photos/Images
  PHOTO_UPLOADED: 'photo_uploaded',
  PHOTO_VIEWED: 'photo_viewed',
  PHOTO_DOWNLOADED: 'photo_downloaded',

  // Messaging
  MESSAGE_SENT: 'message_sent',
  CONVERSATION_STARTED: 'conversation_started',
  CONVERSATION_OPENED: 'conversation_opened',
  BROADCAST_SENT: 'broadcast_sent',

  // Invitations
  INVITATION_SENT: 'invitation_sent',
  INVITATION_ACCEPTED: 'invitation_accepted',
  INVITATION_DECLINED: 'invitation_declined',
  INVITATION_REVOKED: 'invitation_revoked',
  INVITATION_RESENT: 'invitation_resent',

  // Child management
  CHILD_ADDED: 'child_added',
  CHILD_PROFILE_VIEWED: 'child_profile_viewed',
  CHILD_PROFILE_UPDATED: 'child_profile_updated',
  CHILD_PHOTO_UPLOADED: 'child_photo_uploaded',
  CHILD_PHOTO_DELETED: 'child_photo_deleted',

  // Attendance
  CHILD_CHECKED_IN: 'child_checked_in',
  CHILD_CHECKED_OUT: 'child_checked_out',
  ATTENDANCE_PAGE_VIEWED: 'attendance_page_viewed',

  // Calendar
  CALENDAR_EVENT_CREATED: 'calendar_event_created',
  CALENDAR_EVENT_UPDATED: 'calendar_event_updated',
  CALENDAR_EVENT_DELETED: 'calendar_event_deleted',
  CALENDAR_VIEWED: 'calendar_viewed',
  HOLIDAY_REPORTED: 'holiday_reported',
  SICK_DAY_REPORTED: 'sick_day_reported',

  // Navigation
  PAGE_VIEWED: 'page_viewed',
  DASHBOARD_VIEWED: 'dashboard_viewed',
  TIMELINE_VIEWED: 'timeline_viewed',
  SETTINGS_OPENED: 'settings_opened',

  // Settings & Preferences
  PROFILE_UPDATED: 'profile_updated',
  PASSWORD_CHANGED: 'password_changed',
  PIN_SETUP_COMPLETED: 'pin_setup_completed',
  BIOMETRIC_ENABLED: 'biometric_enabled',
  NOTIFICATION_SETTINGS_CHANGED: 'notification_settings_changed',
  DATA_EXPORTED: 'data_exported',
  ACCOUNT_DELETED: 'account_deleted',

  // Cookie consent
  COOKIE_CONSENT_GIVEN: 'cookie_consent_given',
  COOKIE_PREFERENCES_UPDATED: 'cookie_preferences_updated',

  // Subscription & Billing
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  SUBSCRIPTION_UPGRADE_STARTED: 'subscription_upgrade_started',
  SUBSCRIPTION_SEATS_CHANGED: 'subscription_seats_changed',
  PRICING_VIEWED: 'pricing_viewed',
  UPGRADE_CLICKED: 'upgrade_clicked',

  // Errors (non-sensitive)
  ERROR_DISPLAYED: 'error_displayed',

  // Marketing
  LEAD_CAPTURED: 'lead_captured',
  CTA_CLICKED: 'cta_clicked',
} as const

export { posthog }
